import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import Badge from 'kingpin/atoms/Badge';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import SideBySideDiff from 'components/SideBySideDiff';
import TextArea from 'components/Inputs/TextArea';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import { readAccountKbSection } from 'services/agentKb';

import { AccountKnowledgeContext } from './AccountKnowledgeProvider';
import TriggerEditor from './TriggerEditor';
import { describeTrigger } from './constants';

// Knowledge the triage drafter wrote from a thumbs-down, waiting on a human.
// None of it reaches an answer until it is accepted — that is what makes it
// safe for a model to write here at all.

const Pane = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;
`;

const Card = styled.div`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

const EditArea = styled(TextArea)`
  min-height: 180px;
  font-family: monospace;
  font-size: 13px;
  width: 100%;
  margin-bottom: 8px;
`;

const CenteredDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  text-align: center;
`;

const SuggestionCard = ({ section }: { section: AccountKb.Section }) => {
  const { reviewSection, rights } = useContext(AccountKnowledgeContext);
  const { api } = useContext(CloudFunctionClientContext);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [body, setBody] = useState<string>('');
  const [triggers, setTriggers] = useState<AccountKb.Trigger[]>(
    section.triggers,
  );
  const [isWorking, setIsWorking] = useState<boolean>(false);
  // The head carries the LIVE body; the proposal is a revision the head does
  // not point at, so it is fetched.
  const [proposedBody, setProposedBody] = useState<string | null>(null);

  const isAmendment = section.activeRev > 0;

  useEffect(() => {
    let isActive = true;
    readAccountKbSection(api, {
      sectionId: section.id,
      rev: section.proposedRev,
    }).then((result) => {
      if (isActive && result.ok) {
        setProposedBody(result.data.revision.body);
        setBody(result.data.revision.body);
      }
    });
    return () => {
      isActive = false;
    };
  }, [api, section.id, section.proposedRev]);

  const review = async (decision: 'accept' | 'reject') => {
    setIsWorking(true);
    await reviewSection({
      sectionId: section.id,
      decision,
      expectedProposedRev: section.proposedRev,
      ...(isEditing && body.trim() !== '' ? { body } : {}),
      ...(isEditing ? { triggers } : {}),
    });
    setIsWorking(false);
  };

  return (
    <Card data-testid={`account-kb-suggestion-${section.id}`}>
      <Row centerAlign spaceBetween>
        <Row centerAlign style={{ gap: 8 }}>
          <Typography.Body type="Body 13 Bold">{section.title}</Typography.Body>
          <Badge
            badgeType={isAmendment ? 'Orange' : 'Blue'}
            text={isAmendment ? 'Change to a live note' : 'New note'}
          />
          {section.triggers.length === 0 && (
            <Badge badgeType="Red" text="Needs a trigger" />
          )}
        </Row>
        <Row centerAlign style={{ gap: 8 }}>
          <Button
            type="Ghost"
            size="Small"
            label={isEditing ? 'Cancel edit' : 'Edit'}
            isDisabled={!rights.canEdit || isWorking}
            onClick={() => {
              setIsEditing((prev) => !prev);
              setBody(proposedBody ?? section.activeBody);
            }}
          />
          <Button
            type="Secondary"
            size="Small"
            label="Reject"
            isDisabled={!rights.canEdit || isWorking}
            onClick={() => review('reject')}
          />
          <Button
            type="Primary"
            size="Small"
            label={isEditing ? 'Save and accept' : 'Accept'}
            // A triggered section with no triggers can never fire, so accepting
            // one would file knowledge that silently never arrives.
            isDisabled={
              !rights.canEdit ||
              isWorking ||
              (isEditing
                ? triggers.length === 0
                : section.triggers.length === 0)
            }
            isDisabledMessage={
              section.triggers.length === 0
                ? 'Add a trigger first — without one it can never reach an answer'
                : undefined
            }
            onClick={() => review('accept')}
          />
        </Row>
      </Row>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        {section.summary || 'Drafted from feedback'}
        {section.originFeedbackId
          ? ` · feedback ${section.originFeedbackId}`
          : ''}
      </Typography.Body>
      <Row centerAlign style={{ gap: 6, margin: '8px 0' }}>
        {section.triggers.map((trigger) => (
          <Badge
            key={describeTrigger(trigger)}
            badgeType="Default"
            text={describeTrigger(trigger)}
          />
        ))}
      </Row>

      {isEditing ? (
        <>
          <EditArea
            value={body}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setBody(event.target.value)
            }
            data-testid={`account-kb-suggestion-body-${section.id}`}
          />
          <TriggerEditor triggers={triggers} onChange={setTriggers} />
        </>
      ) : (
        proposedBody !== null && (
          <SideBySideDiff
            currentBody={isAmendment ? section.activeBody : ''}
            proposedBody={proposedBody}
            currentLabel={isAmendment ? 'Live now' : 'Nothing live yet'}
            proposedLabel="Proposed"
          />
        )
      )}
    </Card>
  );
};

const SuggestionsTab = () => {
  const { sections, isLoading } = useContext(AccountKnowledgeContext);
  const pending = sections.filter((section) => section.proposedRev > 0);

  if (isLoading) {
    return (
      <CenteredDiv>
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Loading…
        </Typography.Body>
      </CenteredDiv>
    );
  }

  if (pending.length === 0) {
    return (
      <CenteredDiv>
        <Typography.Body type="Body 13" color="var(--color-text-secondary)">
          Nothing waiting. When someone marks an answer wrong and the fix is a
          fact about this account, a draft appears here.
        </Typography.Body>
      </CenteredDiv>
    );
  }

  return (
    <Pane data-testid="account-kb-suggestions">
      {pending.map((section) => (
        <SuggestionCard key={section.id} section={section} />
      ))}
    </Pane>
  );
};

export default SuggestionsTab;
